import { captureAccount } from "../services/accountStorage";
import React, { useContext, useRef, useState } from "react";
import { ActivityIndicator } from "react-native";
import NetInfo from "@react-native-community/netinfo";
import { View, Text } from "./Themed";
import Pressable from "./Pressable";
import Colors from "../constants/Colors";
import GlobalContext from "../context/Global";
import useSyncOpenHouses from "../hooks/useSyncOpenHouses";
import { getLocalData } from "../services/storage";
import { syncOpenHouseAttendees } from "../services/attendeeSubmission";
import { classifyFailure, FailureReason } from "../utils/networkHealth";

const failureMessage = (reason?: FailureReason) => {
  if (reason === "auth") return "Your session could not be verified. Your pending changes are saved; sign in again to retry.";
  if (reason === "server") return "The server could not sync your changes. They are saved on this device. Please try again later.";
  if (reason === "network" || reason === "timeout") return "The connection could not complete the sync. Your pending changes are saved. Check your connection and try again.";
  return "Some changes could not sync. They are saved on this device. Please try again.";
};

export default function ManualSyncButton({ uuid, activeUuids = [], onSynced }: {
  uuid?: string; activeUuids?: string[]; onSynced?: () => void | Promise<void>;
}) {
  const { user } = useContext(GlobalContext);
  const syncOpenHouses = useSyncOpenHouses();
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const sync = async () => {
    if (inFlight.current) return;
    const assertAccount = captureAccount();
    inFlight.current = true;
    setBusy(true);
    setMessage("Syncing leads and pending changes…");
    try {
      const connection = await NetInfo.fetch();
      if (connection.isConnected === false || connection.isInternetReachable === false) {
        setMessage("You’re offline. Your changes are saved on this device. Connect to the internet and tap Sync again.");
        return;
      }
      if (!user?.id) { setMessage(failureMessage("auth")); return; }
      assertAccount();
      const pending = (await getLocalData("syncData")) || [];
      const pendingIds = new Set(pending.map((item: any) => item.data.uuid));
      assertAccount();
      const result = await syncOpenHouses(uuid);
      let failed = result.failed;
      let reason = result.failureReason;
      const ids = [...new Set(uuid ? [uuid] : activeUuids)].filter(id => !pendingIds.has(id));
      // Limit concurrent requests while preserving each house's delivery lock.
      let next = 0;
      await Promise.all(Array.from({ length: Math.min(3, ids.length) }, async () => {
        while (next < ids.length) {
          assertAccount();
          const id = ids[next++];
          try { await syncOpenHouseAttendees(id, user.id); }
          catch (error) { assertAccount(); failed++; reason = classifyFailure(error); }
        }
      }));
      assertAccount();
      await onSynced?.();
      setMessage(failed ? failureMessage(reason) : result.completed
        ? "Sync complete. Leads are updated and finished open houses have moved to Completed."
        : "Sync complete. Your leads and changes are up to date.");
    } catch (error) {
      setMessage(failureMessage(classifyFailure(error)));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  return (
    <View style={{ paddingHorizontal: 16, paddingVertical: 12, gap: 8 }}>
      <Pressable testID="manual_sync_button" accessibilityRole="button"
        accessibilityLabel={busy ? "Syncing" : "Sync leads and pending changes"}
        accessibilityState={{ disabled: busy, busy }} disabled={busy} onPress={sync}
        style={{ minHeight: 44, borderRadius: 24, borderWidth: 2,
          borderColor: Colors.primary_500, alignItems: "center", justifyContent: "center", flexDirection: "row", gap: 8 }}>
        {busy && <ActivityIndicator color={Colors.primary_500} />}
        <Text lightColor={Colors.primary_500} weight="Bold">{busy ? "Syncing…" : "Sync"}</Text>
      </Pressable>
      {!!message && <Text accessibilityLiveRegion="polite" lightColor={Colors.nuetral_grey_700}>{message}</Text>}
    </View>
  );
}
